
    // IMPORTS
    import { defineComponent, PropType, computed, provide } from 'vue'
    import { useUserStore } from '@/store/user'
    import { buildBomItemLocalization, buildLocalization, buildNumberFormatter } from '@/util'

    import { Card, CardHeader, CardTitle, CardBody } from '@progress/kendo-vue-layout'
    import DocLink from '@/components/common/doc-link.vue'
    import StockIndicator from '@/components/common/stock-indicator.vue'
    import MyIcon from '@/components/common/my-icon.vue'
    import MaterialLinkSection from './material-link-section.vue'
    import MaterialPicture from '@/components/common/material-picture.vue'

    import { DocumentNames, DocumentVisibility } from '@/types/document'
    import { buildPriceFormatter, MaterialListItem } from '@/types/material'

    export default defineComponent({
        name: 'MaterialDetails',

        components: {
            MaterialPicture,
            DocLink,
            Card,
            CardHeader,
            CardTitle,
            CardBody,
            StockIndicator,
            MyIcon,
            MaterialLinkSection
        },

        /********************************************************************************************************************
         PUBLIC EVENTS
         ********************************************************************************************************************/
        emits: {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            newMaterial: (material: MaterialListItem) => true
        },

        /********************************************************************************************************************
         PUBLIC PROPERTIES
         ********************************************************************************************************************/
        props: {
            materialData: { type: Object as PropType<MaterialListItem>, required: true }
        },

        setup(props, { emit }) {
            /********************************************************************************************************************
             PRIVATE DATA
             ********************************************************************************************************************/
            const userStore = useUserStore()

            /********************************************************************************************************************
             PUBLIC DATA
             ********************************************************************************************************************/

            /********************************************************************************************************************
             COMPUTED PROPERTIES
             ********************************************************************************************************************/
            const { priceFormatter } = buildPriceFormatter()
            const numberFormatter = buildNumberFormatter()
            const loginType = computed(() => userStore.loginType)
            const isAdmin = computed(() => userStore.isAdmin)
            const materialNo = computed(() => props.materialData.materialNo)
            const description = computed(() => props.materialData.description)
            const salesText = computed(() => props.materialData.salesText)

            const additionalDocs = computed(() => props.materialData.documents?.filter(doc => doc.documentType != 'RES'))
            const namesOfAdditionalDocs = computed(() => additionalDocs.value?.length ? new DocumentNames(additionalDocs.value).localizedNamesOfDocuments() : undefined)
            const additionalDocsForEveryone = computed(() => namesOfAdditionalDocs.value?.filter(doc => doc.visibility == DocumentVisibility.Everyone))
            const additionalDocsForAdmins = computed(() => namesOfAdditionalDocs.value?.filter(doc => doc.visibility == DocumentVisibility.AdminsOnly))
            const priceData = computed(() => priceFormatter.value(props.materialData).formattedPriceWithUnit())
            const documentsForAdminsSectionTitle = computed(() => `${localize('documents')} (${localize('adminData')?.toLowerCase()})`)
            const showOrderingSection = computed(() => loginType.value == 'user' && props.materialData.isSparePart)

            const computedData = {
                priceFormatter,
                numberFormatter,
                loginType,
                isAdmin,
                materialNo,
                description,
                salesText,
                additionalDocs,
                namesOfAdditionalDocs,
                additionalDocsForEveryone,
                additionalDocsForAdmins,
                priceData,
                documentsForAdminsSectionTitle,
                showOrderingSection
            }

            /********************************************************************************************************************
             PUBLIC METHODS AND EVENT HANDLERS
             ********************************************************************************************************************/
            const { localize } = buildLocalization()
            const { localizeBomItemText } = buildBomItemLocalization()

            function onClick(material: MaterialListItem) {
                emit('newMaterial', material)
            }

            function materialText(item: MaterialListItem | null) {
                return item == null
                    ? ''
                    : `${item.materialNo} ${item.description}`
            }

            const functions = { localize, localizeBomItemText, onClick, materialText }

            // Share common functions with sub components
            provide('materialText', materialText)
            provide('onClick', onClick)

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return {
                ...computedData,
                ...functions
            }
        }
    });
